import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import io from 'socket.io-client';
import Tutorial from './Tutorial';
import './PlayerScreen.css';

// Auto-detect server URL based on how client is accessed
function getServerURL() {
  if (process.env.REACT_APP_SERVER_URL) {
    return process.env.REACT_APP_SERVER_URL;
  }
  const hostname = window.location.hostname;
  const port = process.env.REACT_APP_SERVER_PORT || '3002';
  if (port === '80') {
    return `http://${hostname}`;
  }
  return `http://${hostname}:${port}`;
}

function PlayerScreen() {
  const [searchParams] = useSearchParams();
  const [socket, setSocket] = useState(null);
  const [roomCode, setRoomCode] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [showTutorial, setShowTutorial] = useState(false);
  const [joined, setJoined] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [gameState, setGameState] = useState('LOBBY');
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [buzzed, setBuzzed] = useState(false);
  const [answerResult, setAnswerResult] = useState(null);
  const [revealData, setRevealData] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(15);
  const [hasVoted, setHasVoted] = useState(false);
  const [startVotes, setStartVotes] = useState(0);
  const [requiredVotes, setRequiredVotes] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [pausedBy, setPausedBy] = useState(null);
  const [questionReady, setQuestionReady] = useState(false);
  const [currentRound, setCurrentRound] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showWheelSpinner, setShowWheelSpinner] = useState(false);
  const [wheelPack, setWheelPack] = useState(null);
  const [roulettePosition, setRoulettePosition] = useState(0);
  const [rouletteSelectedIndex, setRouletteSelectedIndex] = useState(-1);
  
  // Bomb Pass mode state
  const [bombHolder, setBombHolder] = useState(null);
  const [isBombHolder, setIsBombHolder] = useState(false);
  
  // Scoreboard state
  const [showScoreboard, setShowScoreboard] = useState(false);
  const [allPlayers, setAllPlayers] = useState([]);
  const [myHealth, setMyHealth] = useState(null); // For Final Countdown
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasRestoredSession, setHasRestoredSession] = useState(false);
  const lastSubmitTimeRef = useRef(0);
  const sessionIdRef = useRef(null);
  const prevGameStateRef = useRef(gameState);
  const audioContextRef = useRef(null);
  const navigate = useNavigate();

  // iOS fullscreen support
  useEffect(() => {
    // Set viewport for iOS
    const viewport = document.querySelector('meta[name=viewport]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
    }
    
    // Prevent iOS bounce/scroll
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  // Generate or restore session ID
  useEffect(() => {
    let sessionId = localStorage.getItem('buzzerbash_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('buzzerbash_session_id', sessionId);
    }
    sessionIdRef.current = sessionId;
    
    const urlRoomCode = searchParams.get('room');
    if (urlRoomCode && !joined && !hasRestoredSession) {
      setRoomCode(urlRoomCode.toUpperCase());
      setHasRestoredSession(true);
      setConnectionError(`Room code ${urlRoomCode.toUpperCase()} loaded. Enter your name to join!`);
      return;
    }
    
    const savedSession = localStorage.getItem('buzzerbash_player_session');
    if (savedSession && !joined && !hasRestoredSession) {
      try {
        const session = JSON.parse(savedSession);
        if (session.timestamp && Date.now() - session.timestamp < 7200000) {
          setRoomCode(session.roomCode || '');
          setPlayerName(session.playerName || '');
          setHasRestoredSession(true);
          if (session.roomCode && session.playerName) {
            setConnectionError(`Welcome back, ${session.playerName}! Tap "Join Game" to reconnect.`);
          }
        } else {
          localStorage.removeItem('buzzerbash_player_session');
        }
      } catch (e) {
        localStorage.removeItem('buzzerbash_player_session');
      }
    }
  }, [joined, hasRestoredSession, searchParams]);

  // Save session when joining
  useEffect(() => {
    if (joined && roomCode && playerName) {
      const session = { roomCode, playerName, sessionId: sessionIdRef.current, timestamp: Date.now() };
      localStorage.setItem('buzzerbash_player_session', JSON.stringify(session));
    }
  }, [joined, roomCode, playerName]);

  // Clear session when game ends
  useEffect(() => {
    if (gameState === 'GAME_END') {
      localStorage.removeItem('buzzerbash_player_session');
    }
  }, [gameState]);

  // Socket event handlers
  useEffect(() => {
    if (joined && socket) {
      socket.on('room-state', (state) => {
        const prevState = prevGameStateRef.current;
        setGameState(state.state);
        
        if (state.state === 'ANSWERING' || state.state === 'REVEAL') {
          setCurrentQuestion(state.currentQuestion);
        } else {
          setCurrentQuestion(null);
        }
        
        setQuestionNumber(state.questionNumber || 0);
        setTotalQuestions(state.totalQuestions || 0);
        setStartVotes(state.startVoteCount || 0);
        setRequiredVotes(Math.max(1, Math.ceil((state.players?.length || 0) / 2)));
        setIsPaused(state.isPaused || false);
        setPausedBy(state.pausedBy);
        setCurrentRound(state.currentRound || null);
        
        // Update all players for scoreboard
        if (state.players) {
          setAllPlayers([...state.players].sort((a, b) => b.score - a.score));
        }
        
        // Update bomb holder for Bomb Pass mode
        if (state.modeState?.bombHolderId) {
          const bombHolderPlayer = state.players?.find(p => p.id === state.modeState.bombHolderId);
          setBombHolder(bombHolderPlayer?.name || null);
          
          // Check if current player is bomb holder (case-insensitive)
          const me = state.players?.find(p => p.name?.toLowerCase() === playerName?.toLowerCase());
          const amIBombHolder = me?.id === state.modeState.bombHolderId;
          setIsBombHolder(amIBombHolder);
          console.log('💣 Room state bomb holder:', bombHolderPlayer?.name, 'Me:', me?.name, 'Match:', amIBombHolder);
        } else {
          setBombHolder(null);
          setIsBombHolder(false);
        }
        
        // Update health for Final Countdown
        if (state.modeState?.healthByPlayerId) {
          const me = state.players?.find(p => p.name === playerName);
          if (me) {
            setMyHealth(state.modeState.healthByPlayerId[me.id] ?? null);
          }
        } else {
          setMyHealth(null);
        }

        if (prevState !== 'GAME_END' && state.state === 'GAME_END') {
          playGameEndSound();
        }
        prevGameStateRef.current = state.state;
        
        const player = state.players?.find(p => p.name === playerName);
        if (player) {
          setScore(player.score);
          setStreak(player.streak);
        }
      });

      socket.on('vote-updated', ({ voteCount, requiredVotes: req }) => {
        setStartVotes(voteCount);
        setRequiredVotes(req);
      });

      socket.on('game-paused', ({ isPaused: paused, pausedBy: by }) => {
        setIsPaused(paused);
        setPausedBy(by);
      });

      // Listen for bomb holder changes
      socket.on('bomb-holder-changed', ({ playerName: holderName }) => {
        console.log('💣 Bomb holder changed:', holderName, 'Me:', playerName, 'Match:', holderName === playerName);
        setBombHolder(holderName);
        // Case-insensitive comparison for bomb holder
        const amIBombHolder = holderName?.toLowerCase() === playerName?.toLowerCase();
        setIsBombHolder(amIBombHolder);
        console.log('💣 Set isBombHolder:', amIBombHolder);
        
        // Play sound when bomb is passed
        if (amIBombHolder) {
          playBombPassSound();
        }
      });

      socket.on('pack-wheel-spin', ({ packId, pack, roundIndex, allPacks }) => {
        if (!allPacks || !Array.isArray(allPacks) || allPacks.length === 0) return;
        if (!pack || !packId) return;
        
        let selectedIndex = allPacks.findIndex(p => p?.id === packId);
        if (selectedIndex === -1) {
          selectedIndex = allPacks.findIndex(p => p?.name === pack?.name);
        }
        if (selectedIndex === -1) selectedIndex = 0;
        
        const packWithId = { ...pack, id: pack.id || packId };
        setWheelPack({ packId, pack: packWithId, roundIndex, allPacks });
        setRouletteSelectedIndex(selectedIndex);
        setRoulettePosition(0);
        setShowWheelSpinner(true);
        
        // Animation
        const totalPacks = allPacks.length;
        const scrollCycles = 3;
        const totalWidth = 220;
        const fastDuration = 2000;
        const slowDuration = 1500;
        const totalDuration = fastDuration + slowDuration;
        let startTime = Date.now();
        
        const animate = () => {
          const elapsed = Date.now() - startTime;
          if (elapsed < fastDuration) {
            const progress = elapsed / fastDuration;
            setRoulettePosition(-progress * (scrollCycles * totalPacks * totalWidth));
            requestAnimationFrame(animate);
          } else if (elapsed < totalDuration) {
            const slowProgress = (elapsed - fastDuration) / slowDuration;
            const easeOut = 1 - Math.pow(1 - slowProgress, 3);
            const fastPosition = scrollCycles * totalPacks * totalWidth;
            const slowPosition = selectedIndex * totalWidth;
            setRoulettePosition(-(fastPosition + (slowPosition - fastPosition) * easeOut));
            requestAnimationFrame(animate);
          } else {
            setRoulettePosition(-selectedIndex * totalWidth);
            setTimeout(() => setShowWheelSpinner(false), 3000);
          }
        };
        requestAnimationFrame(animate);
      });

      socket.on('game-started', ({ questionNumber, totalQuestions, currentRound }) => {
        setQuestionNumber(questionNumber);
        setTotalQuestions(totalQuestions);
        setBuzzed(false);
        setAnswerResult(null);
        setRevealData(null);
        if (currentRound) setCurrentRound(currentRound);
        playGameStartSound();
      });

      socket.on('answering-started', ({ timeLimit }) => {
        setBuzzed(false);
        setAnswerResult(null);
        setTimeLeft(timeLimit);
        setRevealData(null);
        setQuestionReady(false);
      });

      socket.on('question-ready', ({ ready }) => {
        setQuestionReady(ready);
      });

      socket.on('answer-result', ({ isCorrect, points, streak: newStreak, message }) => {
        setAnswerResult({ isCorrect, points });
        setStreak(newStreak);
        setScore(prev => prev + points);
        if (message) console.log(message);
      });

      socket.on('reveal-answer', (data) => {
        setRevealData(data);
      });

      // All players answered - countdown speeds up
      socket.on('all-answered', ({ speedUp }) => {
        console.log('🎉 All players answered!');
        // Could show visual feedback here
      });

      socket.on('next-question', ({ questionNumber, totalQuestions, currentRound }) => {
        setQuestionNumber(questionNumber);
        setTotalQuestions(totalQuestions);
        setBuzzed(false);
        setAnswerResult(null);
        setRevealData(null);
        if (currentRound) setCurrentRound(currentRound);
        playNewQuestionSound();
      });

      socket.on('join-error', ({ message }) => {
        alert(message);
      });
    }
  }, [joined, socket, playerName]);

  // Timer countdown
  useEffect(() => {
    if (gameState === 'ANSWERING' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameState, timeLeft]);

  // Sound effects
  const ensureAudioContext = () => {
    if (!soundEnabled) return null;
    if (!audioContextRef.current) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        audioContextRef.current = new AudioContext();
        audioContextRef.current.resume().catch(() => {});
      }
    }
    return audioContextRef.current;
  };

  const playBeep = (frequency, durationMs = 120, volume = 0.2) => {
    const ctx = ensureAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationMs / 1000);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durationMs / 1000);
  };

  const playCountdownTick = () => {
    const freq = 600 + (5 - Math.min(timeLeft, 5)) * 80;
    playBeep(freq, 80, 0.25);
  };

  const playBuzzSound = () => {
    playBeep(400, 80, 0.3);
    setTimeout(() => playBeep(650, 80, 0.3), 90);
  };

  const playBombPassSound = () => {
    playBeep(300, 150, 0.4);
    setTimeout(() => playBeep(400, 150, 0.35), 160);
    setTimeout(() => playBeep(500, 200, 0.3), 320);
  };

  const playAnswerSound = (isCorrect) => {
    if (isCorrect) {
      playBeep(500, 80, 0.3);
      setTimeout(() => playBeep(650, 80, 0.25), 90);
      setTimeout(() => playBeep(800, 120, 0.2), 190);
    } else {
      playBeep(300, 120, 0.3);
      setTimeout(() => playBeep(200, 160, 0.25), 140);
    }
  };

  const playJoinSound = () => {
    playBeep(500, 80, 0.25);
    setTimeout(() => playBeep(650, 80, 0.22), 90);
    setTimeout(() => playBeep(800, 120, 0.2), 190);
  };

  const playVoteSound = () => playBeep(750, 120, 0.25);
  const playNewQuestionSound = () => {
    playBeep(450, 80, 0.2);
    setTimeout(() => playBeep(650, 120, 0.18), 90);
  };
  const playGameStartSound = () => {
    playBeep(500, 90, 0.28);
    setTimeout(() => playBeep(700, 90, 0.24), 110);
    setTimeout(() => playBeep(900, 140, 0.22), 230);
  };
  const playGameEndSound = () => {
    playBeep(700, 120, 0.28);
    setTimeout(() => playBeep(500, 140, 0.24), 140);
    setTimeout(() => playBeep(400, 200, 0.2), 320);
  };

  // Sound triggers
  useEffect(() => {
    if (gameState === 'ANSWERING' && timeLeft > 0 && timeLeft <= 5) {
      playCountdownTick();
    }
    // eslint-disable-next-line
  }, [timeLeft, gameState]);

  useEffect(() => {
    if (answerResult) playAnswerSound(answerResult.isCorrect);
    // eslint-disable-next-line
  }, [answerResult]);

  // Handlers
  const handleJoin = (e) => {
    e.preventDefault();
    if (!roomCode || !playerName) {
      setConnectionError('Please enter both room code and name');
      return;
    }

    setIsConnecting(true);
    setConnectionError('');
    
    const serverUrl = getServerURL();
    const newSocket = io(serverUrl, {
      transports: ['polling', 'websocket'],
      timeout: 10000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      forceNew: true
    });
    
    setSocket(newSocket);
    
    newSocket.on('connect', () => {
      setIsConnecting(false);
      newSocket.emit('join-room', { roomCode: roomCode.toUpperCase(), playerName });
    });

    newSocket.on('connect_error', () => {
      setIsConnecting(false);
      setConnectionError('Failed to connect. Check your internet connection.');
      newSocket.close();
    });

    newSocket.on('disconnect', () => setIsConnecting(false));

    newSocket.on('join-success', () => {
      setJoined(true);
      setIsConnecting(false);
      playJoinSound();
    });

    newSocket.on('join-error', ({ message }) => {
      setIsConnecting(false);
      setConnectionError(message);
      newSocket.close();
    });
  };

  const handleAnswer = (answerIndex) => {
    const now = Date.now();
    if (now - lastSubmitTimeRef.current < 300) return;
    if (isSubmitting || buzzed) return;
    
    // Check if bomb pass mode and not bomb holder
    if (currentRound?.mode === 'bomb_pass' && !isBombHolder) return;
    
    if (socket && roomCode && gameState === 'ANSWERING' && questionReady) {
      setIsSubmitting(true);
      lastSubmitTimeRef.current = now;
      socket.emit('submit-answer', { roomCode, answerIndex });
      setBuzzed(true);
      playBuzzSound();
      setTimeout(() => setIsSubmitting(false), 500);
    }
  };

  const handleVoteToStart = () => {
    if (socket && roomCode && !hasVoted) {
      socket.emit('vote-to-start', { roomCode });
      setHasVoted(true);
      playVoteSound();
    }
  };

  // Get player rank
  const getMyRank = () => {
    const idx = allPlayers.findIndex(p => p.name === playerName);
    return idx >= 0 ? idx + 1 : '-';
  };

  // ==================== RENDER ====================

  if (!joined) {
    return (
      <div className="player-screen join-screen">
        <div className="join-form">
          <div className="join-header">
            <h1>🎯 Join Game</h1>
            <button className="btn-help-small" onClick={() => setShowTutorial(true)}>❓</button>
          </div>
          <form onSubmit={handleJoin}>
            <div className="form-group">
              <label>Room Code</label>
              <input
                type="text"
                value={roomCode}
                onChange={(e) => { setRoomCode(e.target.value.toUpperCase()); setConnectionError(''); }}
                placeholder="XXXX"
                maxLength="4"
                required
                className="input-code"
                disabled={isConnecting}
                autoComplete="off"
                autoCapitalize="characters"
              />
            </div>
            <div className="form-group">
              <label>Your Name</label>
              <input
                type="text"
                value={playerName}
                onChange={(e) => { setPlayerName(e.target.value); setConnectionError(''); }}
                placeholder="Enter your name"
                maxLength="20"
                required
                disabled={isConnecting}
                autoComplete="off"
              />
            </div>
            {connectionError && (
              <div className={`message-box ${connectionError.includes('Welcome') || connectionError.includes('loaded') ? 'info' : 'error'}`}>
                {connectionError}
              </div>
            )}
            {isConnecting && (
              <div className="connecting-indicator">
                <div className="spinner"></div>
                <span>Connecting...</span>
              </div>
            )}
            <button type="submit" className="btn-join" disabled={isConnecting}>
              {isConnecting ? 'Connecting...' : 'Join Game'}
            </button>
          </form>
          <button className="btn-back" onClick={() => navigate('/')} disabled={isConnecting}>
            ← Back
          </button>
        </div>
        {showTutorial && <Tutorial onClose={() => setShowTutorial(false)} isHost={false} />}
      </div>
    );
  }

  // Main game view
  return (
    <div className="player-screen game-active">
      {/* Top Bar with Score & Scoreboard Toggle */}
      <div className="top-bar">
        <button className="btn-scoreboard-toggle" onClick={() => setShowScoreboard(!showScoreboard)}>
          📊 #{getMyRank()} • {score}pts
          {streak > 0 && <span className="streak-mini">🔥{streak}</span>}
        </button>
        <button className="sound-toggle-mini" onClick={() => setSoundEnabled(!soundEnabled)}>
          {soundEnabled ? '🔊' : '🔇'}
        </button>
      </div>

      {/* Expandable Scoreboard */}
      {showScoreboard && (
        <div className="scoreboard-dropdown">
          <div className="scoreboard-header">
            <span>Live Standings</span>
            <button onClick={() => setShowScoreboard(false)}>✕</button>
          </div>
          <div className="scoreboard-list">
            {allPlayers.map((p, idx) => (
              <div key={p.name} className={`scoreboard-row ${p.name === playerName ? 'me' : ''}`}>
                <span className="rank">{idx + 1}</span>
                <span className="name">{p.name}{p.name === playerName && ' (You)'}</span>
                <span className="pts">{p.score}</span>
                {p.streak > 0 && <span className="streak-badge">🔥{p.streak}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Wheel Spinner Overlay */}
      {showWheelSpinner && wheelPack && wheelPack.allPacks?.length > 0 && (
        <div className="roulette-overlay">
          <div className="roulette-container">
            <div className="roulette-title">🎡 Selecting Topic...</div>
            <div className="roulette-track-wrapper">
              <div className="roulette-track" style={{ transform: `translateX(${roulettePosition}px)` }}>
                {[...Array(5)].flatMap((_, cycle) =>
                  wheelPack.allPacks.map((pack, index) => (
                    <div key={`${cycle}-${pack.id}`} className="roulette-item">
                      <div className="roulette-item-icon">{pack.icon}</div>
                      <div className="roulette-item-name">{pack.name}</div>
                    </div>
                  ))
                )}
              </div>
              <div className="roulette-indicator"></div>
            </div>
          </div>
        </div>
      )}

      {/* Pause Overlay */}
      {isPaused && (
        <div className="pause-overlay">
          <div className="pause-content">
            <h1>⏸️ Paused</h1>
            <p>by {pausedBy}</p>
          </div>
        </div>
      )}

      {/* LOBBY STATE */}
      {gameState === 'LOBBY' && (
        <div className="lobby-view-player">
          <h2>Waiting to start...</h2>
          <div className="player-info-card">
            <p>Room: <strong>{roomCode}</strong></p>
            <p>You: <strong>{playerName}</strong></p>
          </div>
          <div className="vote-section-player">
            <p className="vote-count">{startVotes} / {requiredVotes} votes</p>
            <div className="vote-bar">
              <div className="vote-fill" style={{ width: `${(startVotes / requiredVotes) * 100}%` }} />
            </div>
            <button className="btn-vote-big" onClick={handleVoteToStart} disabled={hasVoted}>
              {hasVoted ? '✓ Voted!' : 'Vote to Start'}
            </button>
          </div>
        </div>
      )}

      {/* ANSWERING STATE */}
      {gameState === 'ANSWERING' && (
        <div className={`answering-view-ios ${currentRound?.mode === 'bomb_pass' ? 'bomb-mode' : ''}`}>
          {/* Mode & Timer Bar */}
          <div className="mode-timer-bar">
            <div className="mode-badge-small">
              {currentRound?.icon} {currentRound?.name || 'Trivia'}
            </div>
            <div className={`timer-badge ${timeLeft <= 5 ? 'urgent' : ''}`}>
              ⏱️ {timeLeft}s
            </div>
          </div>

          {/* Bomb Pass Mode - Bomb Holder Indicator */}
          {currentRound?.mode === 'bomb_pass' && (
            <div className={`bomb-holder-banner ${isBombHolder ? 'you-have-bomb' : 'someone-else'}`}>
              {isBombHolder ? (
                <>💣 YOU HAVE THE BOMB! Answer to pass it!</>
              ) : bombHolder ? (
                <>💣 {bombHolder} has the bomb</>
              ) : (
                <>💣 Waiting for bomb holder...</>
              )}
            </div>
          )}

          {/* Final Countdown Health */}
          {currentRound?.mode === 'final_countdown' && myHealth !== null && (
            <div className="health-banner">
              <span>❤️ Health: {myHealth}/10</span>
              <div className="health-bar-mini">
                <div className="health-fill" style={{ width: `${(myHealth / 10) * 100}%` }} />
              </div>
            </div>
          )}

          {/* Question */}
          {currentQuestion ? (
            <>
              <div className="question-container-ios">
                <p className="q-number">Q{questionNumber}/{totalQuestions}</p>
                <h2 className="question-text-ios">{currentQuestion.question}</h2>
              </div>

              {/* Status Message */}
              {!questionReady ? (
                <div className="status-message">⏳ Get ready...</div>
              ) : currentRound?.mode === 'bomb_pass' && !isBombHolder ? (
                <div className="status-message disabled-message">
                  🔒 Only {bombHolder} can answer
                </div>
              ) : buzzed ? (
                <div className="status-message submitted-message">
                  {answerResult?.isCorrect ? '✅ Correct!' : '📝 Submitted!'}
                  {answerResult && (
                    <span className={answerResult.points >= 0 ? 'pts-pos' : 'pts-neg'}>
                      {answerResult.points >= 0 ? '+' : ''}{answerResult.points}
                    </span>
                  )}
                </div>
              ) : (
                <div className="status-message ready-message">Tap your answer!</div>
              )}

              {/* Answer Grid - 2x2 for iOS */}
              <div className="answer-grid-ios">
                {currentQuestion.answers.map((answer, index) => {
                  const canAnswer = questionReady && !buzzed && !isSubmitting;
                  const isBombRestricted = currentRound?.mode === 'bomb_pass' && !isBombHolder;
                  const isDisabled = !canAnswer || isBombRestricted;
                  
                  return (
                    <button
                      key={index}
                      className={`answer-btn-ios 
                        ${buzzed ? 'answered' : ''} 
                        ${isBombRestricted ? 'bomb-restricted' : ''}
                        ${isDisabled ? 'disabled' : 'active'}
                      `}
                      onClick={() => !isDisabled && handleAnswer(index)}
                      disabled={isDisabled}
                    >
                      <span className="answer-letter">{String.fromCharCode(65 + index)}</span>
                      <span className="answer-text">{answer}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="loading-question">Loading...</div>
          )}
        </div>
      )}

      {/* REVEAL STATE */}
      {gameState === 'REVEAL' && revealData && currentQuestion && (
        <div className="reveal-view-ios">
          <div className="reveal-header">
            <span>Q{questionNumber}/{totalQuestions}</span>
          </div>
          <h2 className="reveal-question">{currentQuestion.question}</h2>
          <div className="reveal-answers">
            {currentQuestion.answers.map((answer, index) => (
              <div
                key={index}
                className={`reveal-answer ${index === revealData.correctAnswer ? 'correct' : ''}`}
              >
                <span className="letter">{String.fromCharCode(65 + index)}</span>
                <span className="text">{answer}</span>
                {index === revealData.correctAnswer && <span className="check">✓</span>}
              </div>
            ))}
          </div>
          <div className="reveal-score">
            Your Score: <strong>{score}</strong>
            {streak > 0 && <span className="streak-reveal">🔥 {streak}</span>}
          </div>
        </div>
      )}

      {/* GAME END STATE */}
      {gameState === 'GAME_END' && (
        <div className="game-end-view-player">
          <h1>🎉 Game Over!</h1>
          <div className="final-rank">
            You finished <strong>#{getMyRank()}</strong> with <strong>{score}</strong> points!
          </div>
          <div className="final-standings">
            {allPlayers.slice(0, 5).map((p, idx) => (
              <div key={p.name} className={`final-row ${p.name === playerName ? 'me' : ''}`}>
                <span className="medal">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}</span>
                <span className="name">{p.name}</span>
                <span className="pts">{p.score}</span>
              </div>
            ))}
          </div>
          <button className="btn-play-again" onClick={() => window.location.reload()}>
            Play Again
          </button>
        </div>
      )}
    </div>
  );
}

export default PlayerScreen;
